import { useMutation } from '@tanstack/react-query';

import {
  REPORT_SCHEDULE,
  LRT_SEARCH_ENGINE,
  type LocationId,
  type CountryISO3Code,
  CANADA_ISO_CODE,
  USA_ISO_CODE
} from '@brightlocal/shared';

import { apiClient } from 'shared/apiClient';
import type { ErrorReporting, Feature } from 'shared/utils';

import { REPORT_CREATE_STEPS, REPORT_CREATE_TOOLS } from '../consts';
import { buildReportCreateErrorReport, runReportStep } from '../utils';

// Default search engines we want to use for LRT report
const DEFAULT_LRT_SEARCH_ENGINES = [
  LRT_SEARCH_ENGINE.GOOGLE,
  LRT_SEARCH_ENGINE.GOOGLE_MOBILE,
  LRT_SEARCH_ENGINE.GOOGLE_LOCAL_PACK,
  LRT_SEARCH_ENGINE.GOOGLE_PLACES
] as const;

// Default schedule we want to use for LRT report
const DEFAULT_LRT_SCHEDULE = REPORT_SCHEDULE.ADHOC;

// Default time zone. As we run the report as adhoc this isn't used, but it's still required by the API
const DEFAULT_LRT_TIME_ZONE = 'America/New_York';

const TOOL = REPORT_CREATE_TOOLS.LRT;
const METHOD = 'apiClient.lrt.createReport / apiClient.lrt.runReport';

interface UseLRTReportMutationOptions {
  feature: Feature;
}

export interface LRTReportMutationVariables {
  /** The ID of the location to run the report for */
  locationId: LocationId;
  /** The keywords to run the report for */
  keywords: string[];
  /** The location details using to run the report */
  location: {
    /** Location country */
    country: CountryISO3Code;
    /** Location website URL */
    websiteUrl: string;
    /** Location city */
    city?: string;
    /** Location state */
    state?: string;
    /** Location language */
    language?: string;
  };
}

export const useLRTReportMutation = ({ feature }: UseLRTReportMutationOptions) =>
  useMutation({
    mutationFn: async ({ locationId, keywords, location }: LRTReportMutationVariables) => {
      const { country, city, state, language, websiteUrl } = location;
      // Canada locations pass a language code as the country value (e.g. "CAN:EN").
      // Use || so that an empty string also falls back to USA_ISO_CODE.
      const resolvedCountry = country === CANADA_ISO_CODE ? language || USA_ISO_CODE : country;
      const context = { locationId, country, language };

      const { reportId } = await runReportStep({
        tool: TOOL,
        step: REPORT_CREATE_STEPS.CREATE,
        context,
        request: () =>
          apiClient.lrt.createReport({
            location_id: locationId,
            search_engines: [...DEFAULT_LRT_SEARCH_ENGINES],
            schedule: DEFAULT_LRT_SCHEDULE,
            search_location: [city, state, country].filter(Boolean).join(', '),
            keywords,
            run_time_zone: DEFAULT_LRT_TIME_ZONE,
            country: resolvedCountry,
            website_addresses: [websiteUrl]
          })
      });

      await runReportStep({
        tool: TOOL,
        step: REPORT_CREATE_STEPS.RUN,
        context,
        request: () => apiClient.lrt.runReport(reportId)
      });
    },
    meta: {
      errorReporting: {
        feature,
        errorMessage: 'Error creating LRT report',
        method: METHOD,
        resolve: (error) => buildReportCreateErrorReport({ tool: TOOL, feature, method: METHOD, error })
      } satisfies ErrorReporting
    }
  });
